/* eslint-disable import/no-extraneous-dependencies */
import { createApp } from 'vue';
import * as Sentry from '@sentry/vue';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-vue';
import Swal from 'sweetalert2';
import Toast, { useToast } from 'vue-toastification';
import 'vue-toastification/dist/index.css';
import VueApexCharts from 'vue3-apexcharts';
import VueGoogleMaps from '@fawmi/vue-google-maps';
import store from '@/store';
import router from '@/router';
import App from '@/App.vue';
import appEnvConfig, { sentryConfig } from '@/config';
import { GlobalComponentPlugin } from '@/plugins/global-component-plugin';
import { DynamicPluginAdapter } from '@/plugins/dynamic-plugin';
import { RsUtilitiesPlugin } from '@/plugins/utilities-plugin';
import { addScriptTagToHead } from './shared/helpers/add-script-tag-to-head';
import { i18nPlugin } from './i18n';

// Initialize Axios interceptors early to prevent circular dependency errors
import '@/shared/axios/index';
import 'bootstrap/dist/css/bootstrap.css';
import 'sweetalert2/dist/sweetalert2.min.css';
import 'overlayscrollbars/styles/overlayscrollbars.css';
import '@yaireo/tagify/dist/tagify.css';
import '@/styles/global.scss';

addScriptTagToHead(appEnvConfig.fpnLatestScriptUrl);
addScriptTagToHead(appEnvConfig.fpnV2ScriptUrl);
addScriptTagToHead(appEnvConfig.fpnV25ScriptUrl);

const app = createApp(App);

// Plugins
app.use(Toast, { position: 'bottom-left', timeout: 4000, maxToasts: 5, transition: 'Vue-Toastification__fade' });
// vue-toastification@2 only exposes the useToast() composable — no more Vue.prototype.$toast
// auto-injection — so wire it onto globalProperties ourselves to keep `this.$toast` working.
app.config.globalProperties.$toast = useToast();
app.use(VueApexCharts);
// vue-sweetalert2 (Vue 2-only) replaced with sweetalert2 directly — no plugin registration,
// just assign the Swal object to globalProperties to keep `this.$swal.fire(...)` working.
app.config.globalProperties.$swal = Swal;
app.use(VueGoogleMaps, {
  load: {
    key: window?.APP_CONFIG?.VUE_APP_GMAPS_API_KEY,
    libraries: 'places',
    // Render Canadian place names (e.g. "Lake Ontario") regardless of the viewer's locale
    region: 'CA',
  },
});
app.use(router);
app.use(store);
app.use(i18nPlugin);
app.use(RsUtilitiesPlugin);
app.use(DynamicPluginAdapter);
app.use(GlobalComponentPlugin);

// Components
app.component('OverlayScrollbars', OverlayScrollbarsComponent);

if (
  ['staging', 'production'].includes(window?.APP_CONFIG?.VUE_APP_ENV)
  && window?.location.hostname !== 'localhost'
) {
  Sentry.init({
    app,
    ...sentryConfig,
  });
}

const vm = app.mount('#app');

export const getVueApp = () => app;

export default () => vm;
